import { useCallback, useEffect, useRef, useState } from 'react';
import { Icon } from '../../ui/index.js';

/**
 * Small icon button that copies a plain-text value (e-mail, username).
 * Ids keep using IdMono, which already carries its own copy affordance;
 * this is for values that should stay in the regular (sans) font.
 */
export function CopyButton({
  value,
  label,
}: {
  readonly value: string;
  /** Accessible name, e.g. "E-postayı kopyala". */
  readonly label: string;
}): JSX.Element {
  const [copied, setCopied] = useState(false);
  const timer = useRef<number | null>(null);

  useEffect(
    () => () => {
      if (timer.current !== null) window.clearTimeout(timer.current);
    },
    [],
  );

  const onCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      if (timer.current !== null) window.clearTimeout(timer.current);
      timer.current = window.setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard rejected — silent; the value is still selectable */
    }
  }, [value]);

  return (
    <button
      type="button"
      // `copy-btn` opts out of the legacy global <button> skin in styles.css.
      className="copy-btn uinspect-copy"
      onClick={onCopy}
      aria-label={label}
      title={copied ? 'Kopyalandı' : label}
    >
      {copied ? <Icon.Check size={12} /> : <Icon.Copy size={12} />}
    </button>
  );
}
